import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const auth = token => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} });

// -- customers ---------------------------------------------------------------

/**
 * GET /api/customers/ filters on `phone` (prefix match) or `name` (contains) — it has
 * no `search` parameter, so sending one was ignored and the caller got the first 20
 * customers whatever it typed. A digits-only term is a phone number; anything else is
 * a name.
 */
export const listCustomers = (search, token) => {
  const term = (search ?? "").toString().trim();
  const params = term ? (/^\d+$/.test(term) ? { phone: term } : { name: term }) : {};
  return axios.get(`${API}/customers/`, { ...auth(token), params }).then(r => r.data);
};

export const getCustomer = (customerId, token) =>
  axios.get(`${API}/customers/${customerId}`, auth(token)).then(r => r.data);

export const createCustomer = (body, token) =>
  axios.post(`${API}/customers/`, body, auth(token)).then(r => r.data);

export const updateCustomer = (customerId, patch, token) =>
  axios.patch(`${API}/customers/${customerId}`, patch, auth(token)).then(r => r.data);

export const customerOrders = (customerId, token) =>
  axios.get(`${API}/customers/${customerId}/orders`, auth(token)).then(r => r.data);

export const customerLoyalty = (customerId, token) =>
  axios.get(`${API}/customers/${customerId}/loyalty`, auth(token)).then(r => r.data);

/**
 * Manual points adjustment. `points` is signed and `reason` is required — this is the
 * only loyalty entry a human invents, so it has to say why.
 */
export const adjustLoyalty = (customerId, points, reason, token) =>
  axios
    .post(`${API}/customers/${customerId}/loyalty/adjust`, { points, reason }, auth(token))
    .then(r => r.data);

// -- tiers --------------------------------------------------------------------

export const listTiers = token =>
  axios.get(`${API}/customer-tiers`, auth(token)).then(r => r.data);

export const assignTier = (customerId, tierId, token) =>
  axios
    .patch(`${API}/customer-tiers/assign`, { customer_id: customerId, tier_id: tierId }, auth(token))
    .then(r => r.data);

// -- khata --------------------------------------------------------------------

export const listKhataAccounts = token =>
  axios.get(`${API}/khata/accounts`, auth(token)).then(r => r.data);

export const createKhataAccount = (body, token) =>
  axios.post(`${API}/khata/accounts`, body, auth(token)).then(r => r.data);

export const khataAccount = (accountId, token) =>
  axios.get(`${API}/khata/accounts/${accountId}`, auth(token)).then(r => r.data);

export const khataEntries = (accountId, token) =>
  axios.get(`${API}/khata/accounts/${accountId}/entries`, auth(token)).then(r => r.data);

/** Record money received against the tab. Reduces the outstanding balance. */
export const recordKhataPayment = (accountId, body, token) =>
  axios.post(`${API}/khata/accounts/${accountId}/payment`, body, auth(token)).then(r => r.data);

export const recordKhataCharge = (accountId, body, token) =>
  axios.post(`${API}/khata/accounts/${accountId}/charge`, body, auth(token)).then(r => r.data);

// -- campaigns and messages ----------------------------------------------------

export const listCampaigns = token =>
  axios.get(`${API}/campaigns`, auth(token)).then(r => r.data);

export const createCampaign = (body, token) =>
  axios.post(`${API}/campaigns`, body, auth(token)).then(r => r.data);

/**
 * Queues one message per resolved recipient. Nothing is delivered: a LAN-only POS has
 * no SMS gateway, so messages sit at status 'pending' until one is configured. The UI
 * must say "queued", never "sent".
 */
export const scheduleCampaign = (campaignId, scheduledFor, token) =>
  axios
    .post(`${API}/campaigns/${campaignId}/schedule`, { scheduled_for: scheduledFor }, auth(token))
    .then(r => r.data);

export const cancelCampaign = (campaignId, token) =>
  axios.post(`${API}/campaigns/${campaignId}/cancel`, {}, auth(token)).then(r => r.data);

/** Messages the campaign generated. */
export const campaignMessages = (campaignId, token) =>
  axios.get(`${API}/campaigns/${campaignId}/messages`, auth(token)).then(r => r.data);

// The message queue is its own router at /messages, not nested under /campaigns —
// ad-hoc messages have no campaign.
export const listMessages = (status, token) =>
  axios
    .get(`${API}/messages`, { ...auth(token), params: status ? { status } : {} })
    .then(r => r.data);

export const queueMessage = (body, token) =>
  axios.post(`${API}/messages`, body, auth(token)).then(r => r.data);

// -- feedback ------------------------------------------------------------------

export const listFeedback = (params, token) =>
  axios.get(`${API}/feedback`, { ...auth(token), params: params || {} }).then(r => r.data);

export const feedbackSummary = token =>
  axios.get(`${API}/feedback/summary`, auth(token)).then(r => r.data);

export const updateFeedback = (feedbackId, patch, token) =>
  axios.patch(`${API}/feedback/${feedbackId}`, patch, auth(token)).then(r => r.data);
